/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { MAXIMUM_BUSINESS_SEATS, MINIMUM_BUSINESS_SEATS, MINIMUM_BUSINESS_SEATS_STEP, getSubscription, toValidSeats } from '../../../../shared/subscriptions';
import type { FossaPlanName, FossaSubscriptionWithPlanName } from '../../../../shared/subscriptions';
import SubscriptionStore from '../../../redux/subscriptions';
import { openInNewTab } from '../../../utils';
import { LabeledTextInput } from '../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';
import Flex from '../../generic/Flex';
import FormGroup from '../../generic/Forms/FormGroup';
import LabeledNumberInput from '../../generic/Forms/LabeledNumberInput';
import Loader from '../../generic/Loader';
import Text from '../../generic/Text';
import CreditCardComponent from './CreditCardComponent';
import SubscriptionPath, { renderPriceString } from './SubscriptionPath';
import useSubscription from '../../../hooks/subscriptions/useSubscription';

type PaymentComponentProps = {
  newPlan: FossaSubscriptionWithPlanName;
  developers?: number;
  title?: string;
};

type PaymentModalProps = {
onHide: () => void;
  show?: boolean;
} & PaymentComponentProps;

export const PaymentModal = (props: PaymentModalProps) => (
  <Modal onHide={props.onHide} show={props.show} bsSize="large" className="payment-modal">
    <Modal.Header closeButton style={{ minHeight: 0, padding: 0 }} />
    <Modal.Body>
      <PaymentComponent {...props} />
    </Modal.Body>
  </Modal>
);

/**
 * Self contained payment component. Reloads the page on successful subscription change
 *
 * @param {PaymentComponentProps} props - Component props.
 * @param {newPlan} props.FossaSubscriptionWithPlanName - New plan that user is upgrading to (Use getSubscription)
 * @param {title} props.string - Optional, header title. Defaults to "Upgrade Subscription"
 * @param {developers} props.number - Optional, new developer count. Defaults to current count on subscription
 */
const PaymentComponent = ({
  newPlan,
  developers = 0,
  title = 'Update Subscription',
}: PaymentComponentProps) => {
  // Setup external deps/utilities
  const dispatch = useDispatch();
  const { updateSubscription } = useSubscription();

  // Setup state
  const subscription = useSelector(SubscriptionStore.selectors.getSubscription);
  const status = useSelector(SubscriptionStore.selectors.getLoading);
  const [devCount, setDevCount] = useState(developers);
  const [hasPayment, setHasPayment] = useState(false);
  const [isEditingPayment, setIsEditingPayment] = useState(false);

  // Fetch subscription if it doesn't exist or update hasPayment state
  useEffect(() => {
    if (!subscription) {
      dispatch(SubscriptionStore.actions.fetchSubscription());
      return;
    }

    setHasPayment(subscription.hasPaymentMethod);

    if (!developers) {
      setDevCount(subscription.quantity);
    }
  }, [dispatch, subscription, developers]);

  // Setup callback for submitting orders
  const onSubmitOrder = useCallback(async () => {
    updateSubscription({
      plan: newPlan.planName,
      contributors: devCount,
    });
  }, [devCount, newPlan, updateSubscription]);

  const oldPlan = getSubscription({ plan: subscription?.plan as FossaPlanName });

  return (
    <div className="payment-component">
      <Text.H2>
        {title}
      </Text.H2>
      { !subscription || status.loading ? (
        <div className="loading">
          <Flex>
            <Loader size={75} />
          </Flex>
        </div>
      ) : (
        <section>
          <SubscriptionPath newPlan={newPlan} oldPlan={oldPlan} />
          <FormGroup direction="row">
            <LabeledNumberInput
              label="Developer count"
              value={devCount}
              min={MINIMUM_BUSINESS_SEATS}
              max={MAXIMUM_BUSINESS_SEATS}
              step={MINIMUM_BUSINESS_SEATS_STEP}
              onChange={(val) => setDevCount(toValidSeats(val))}
            />
            <LabeledTextInput
              label={`Calculated Price (${renderPriceString(newPlan.price)})`}
              value={`$${(newPlan.price * devCount).toFixed(2)}`}
              onKeyDown={(e) => e.preventDefault()}
            />
          </FormGroup>
          {devCount > 150 &&
          <p className="dev-count-error">
            For more than {MAXIMUM_BUSINESS_SEATS} developers, please
            <Button type={BUTTON_TYPE.ANCHOR} size={BUTTON_SIZE.MEDIUM} onClick={() => openInNewTab('//fossa.com/request-demo')}>contact us</Button>
            for enterprise pricing.
          </p>}
          <CreditCardComponent
            isEditing={isEditingPayment}
            onChange={(change) => setHasPayment(change.complete)}
            onEdit={(isEditing => setIsEditingPayment(isEditing))}
            paymentError={status.errors?.[0] || ''}
          />
          <small>
            If you import all your repos across your whole team, expect to pay up to{' '}
            <b>${(newPlan.price * devCount).toFixed(2)}</b>/mo before credit/discounts. If you
            want to track many external contributors, contact us.
          </small>
          <Flex justify="right">
            <Button type="primary" size={BUTTON_SIZE.MEDIUM} isDisabled={!hasPayment || devCount > 150} onClick={onSubmitOrder}>Update</Button>
          </Flex>
        </section>
      )}
    </div>
  );
};

export default PaymentComponent;
